// Insert Chart, on the EAV tab: EAV's counterpart of LSEG Workspace's button.
// LSEG inserts its own live chart control for the instruments picked; EAV adds
// a sheet whose A1 holds an EAV.RHISTORY formula (a year of daily closes, one
// date column and one column per instrument), waits for the data, then puts
// an Excel line chart over it. Refresh updates the data like any EAV formula.
//
// The flow (insertChart) only talks to a ChartPort, so it is tested without
// Excel; excelChartPort is the Office.js side.

/** LSEG warns above this many instruments; EAV refuses, with the reason in the panel. */
export const MAX_CHART_INSTRUMENTS = 10;

export const NO_INSTRUMENTS =
  "Insert Chart: select the cells holding the instruments (RICs) first, then click Insert Chart again.";
export const TOO_MANY_INSTRUMENTS = `Insert Chart: at most ${MAX_CHART_INSTRUMENTS} instruments per chart. Select fewer and try again.`;

/**
 * What Excel shows in a cell instead of a value: an error, or a function still
 * computing. Office.js may give the code without its "!" (a computing EAV
 * formula read "#BUSY", not "#BUSY!", in Excel 2026-11-01).
 */
const EXCEL_ERROR = /^#(?:N\/A|VALUE|REF|NAME|DIV\/0|NUM|NULL|SPILL|CALC|FIELD|BUSY|GETTING_DATA|BLOCKED|CONNECT|UNKNOWN)[!?]?$/;

/** Whether a cell value is an Excel error or a function still computing (EXCEL_ERROR), not a value. */
export const isExcelError = (v: unknown): boolean => typeof v === "string" && EXCEL_ERROR.test(v);

/**
 * The instruments of the selection: its text cells, row by row, trimmed and
 * without repeats. Numbers, dates, blanks and Excel errors are not instruments.
 */
export function pickInstruments(values: unknown[][]): { instruments: string[] } | { notice: string } {
  const instruments: string[] = [];
  for (const row of values) {
    for (const v of row) {
      if (typeof v !== "string") continue;
      const text = v.trim();
      if (!text || EXCEL_ERROR.test(text) || instruments.includes(text)) continue;
      instruments.push(text);
    }
  }
  if (instruments.length === 0) return { notice: NO_INSTRUMENTS };
  if (instruments.length > MAX_CHART_INSTRUMENTS) return { notice: TOO_MANY_INSTRUMENTS };
  return { instruments };
}

/** "EAV Chart 1", or the first number no sheet of the workbook has yet (Excel compares names case-insensitively). */
export function chartSheetName(existing: string[]): string {
  const taken = new Set(existing.map((n) => n.toLowerCase()));
  let n = 1;
  while (taken.has(`eav chart ${n}`)) n++;
  return `EAV Chart ${n}`;
}

/**
 * The day one year before `today`, as RHistory's START wants it (yyyy-mm-dd).
 * A fixed date, not a relative one: LSEG refuses a signed relative START
 * (-1Y), and a TODAY()-based start would refetch on every recalculation.
 * 29 February gives 28 February.
 */
export function oneYearBefore(today: Date): string {
  const y = today.getFullYear() - 1;
  const m = today.getMonth();
  const last = new Date(y, m + 1, 0).getDate();
  const d = Math.min(today.getDate(), last);
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** The sheet's formula: daily closes from `start` to today, dates down column A, one column per instrument. */
export function historyFormula(instruments: string[], start: string): string {
  const text = `"${instruments.join(";").replace(/"/g, '""')}"`;
  return `=EAV.RHISTORY(${text},".Timestamp;.Close","START:${start} END:0D INTERVAL:1D",,"TSREPEAT:NO SORT:ASC")`;
}

/**
 * What the formula has written so far, from the sheet's values (A1 at [0][0]):
 * `pending` while A1 is still computing, LSEG's text when A1 is one (the
 * request failed), else the block's size: the rows whose column A is a date,
 * and the columns.
 */
export function chartData(values: unknown[][]): { pending: true } | { error: string } | { rows: number; cols: number } {
  const a1 = values[0]?.[0];
  if (a1 === undefined || a1 === null || a1 === "" || (typeof a1 === "string" && EXCEL_ERROR.test(a1))) return { pending: true };
  if (typeof a1 === "string") return { error: a1 };
  let rows = 0;
  while (rows < values.length && typeof values[rows][0] === "number") rows++;
  return { rows, cols: values[0].length };
}

/** The Excel side of Insert Chart. */
export interface ChartPort {
  selectedValues(): Promise<unknown[][]>;
  sheetNames(): Promise<string[]>;
  /** Add the sheet, write `formula` in A1 and show the sheet. */
  addSheetWithFormula(sheet: string, formula: string): Promise<void>;
  /** The sheet's values from A1 to the end of what it uses ([] when empty). */
  sheetValues(sheet: string): Promise<unknown[][]>;
  /** A line chart of columns B.. over the dates of column A, rows 1..rows; one series per instrument. */
  addLineChart(sheet: string, rows: number, cols: number, names: string[]): Promise<void>;
}

export interface InsertChartOptions {
  today?: Date;
  pollMs?: number;
  timeoutMs?: number;
  sleep?: (ms: number) => Promise<void>;
}

/**
 * Insert Chart: the selection's instruments, a new sheet with their history
 * formula, then the chart once the data has arrived and stopped growing (two
 * reads in a row with the same size). Anything that stops it is said in the
 * panel through `notify`.
 */
export async function insertChart(port: ChartPort, notify: (text: string) => void, opts: InsertChartOptions = {}): Promise<void> {
  const { today = new Date(), pollMs = 1000, timeoutMs = 90_000 } = opts;
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));

  const pick = pickInstruments(await port.selectedValues());
  if ("notice" in pick) {
    notify(pick.notice);
    return;
  }
  const sheet = chartSheetName(await port.sheetNames());
  await port.addSheetWithFormula(sheet, historyFormula(pick.instruments, oneYearBefore(today)));

  let last: { rows: number; cols: number } | null = null;
  for (let waited = 0; waited <= timeoutMs; waited += pollMs) {
    await sleep(pollMs);
    const data = chartData(await port.sheetValues(sheet));
    if ("error" in data) {
      notify(`Insert Chart: ${data.error} (the formula stays on ${sheet}).`);
      return;
    }
    if ("pending" in data) continue;
    if (data.rows < 2) {
      notify(`Insert Chart: not enough history to draw a line (the formula stays on ${sheet}).`);
      return;
    }
    if (last && last.rows === data.rows && last.cols === data.cols) {
      await port.addLineChart(sheet, data.rows, data.cols, pick.instruments);
      return;
    }
    last = data;
  }
  notify(`Insert Chart: no data arrived within ${Math.round(timeoutMs / 1000)} seconds (the formula stays on ${sheet}).`);
}

/** ChartPort on Office.js (ExcelApi 1.8 for displayBlanksAs, 1.7 for setXAxisValues; the manifest asks for 1.12). */
export const excelChartPort: ChartPort = {
  selectedValues: () =>
    Excel.run(async (context) => {
      const range = context.workbook.getSelectedRange().load("values");
      await context.sync();
      return range.values;
    }),

  sheetNames: () =>
    Excel.run(async (context) => {
      const sheets = context.workbook.worksheets.load("items/name");
      await context.sync();
      return sheets.items.map((s) => s.name);
    }),

  addSheetWithFormula: (sheet, formula) =>
    Excel.run(async (context) => {
      const ws = context.workbook.worksheets.add(sheet);
      ws.getRange("A1").formulas = [[formula]];
      ws.activate();
      await context.sync();
    }),

  sheetValues: (sheet) =>
    Excel.run(async (context) => {
      const used = context.workbook.worksheets.getItem(sheet).getUsedRangeOrNullObject(true).load(["values", "rowIndex", "columnIndex"]);
      await context.sync();
      if (used.isNullObject || used.rowIndex !== 0 || used.columnIndex !== 0) return [];
      return used.values;
    }),

  addLineChart: (sheet, rows, cols, names) =>
    Excel.run(async (context) => {
      const ws = context.workbook.worksheets.getItem(sheet);
      const dates = ws.getRangeByIndexes(0, 0, rows, 1);
      const chart = ws.charts.add(Excel.ChartType.line, ws.getRangeByIndexes(0, 1, rows, cols - 1), Excel.ChartSeriesBy.columns);
      chart.title.text = "Close";
      chart.displayBlanksAs = Excel.ChartDisplayBlanksAs.interplotted;
      chart.setPosition(ws.getCell(1, cols + 1), ws.getCell(21, cols + 10));
      const series = chart.series.load("items");
      await context.sync();
      series.items.forEach((s, i) => {
        s.name = names[i] ?? `Series ${i + 1}`;
        s.setXAxisValues(dates);
      });
      await context.sync();
    }),
};
